import React from "react";
import { useNavigate } from "react-router-dom";

interface RelatedMember {
  id: number;
  name: string;
  phone?: string;
  imagePath?: string;
  memberType?: string;
  profile?: {
    gender?: string;
    date_of_birth?: string;
  };
}

interface RelatedNonMember {
  id: number;
  name: string;
  phone?: string;
  imagePath?: string;
  memberType?: string;
}

interface Marriage {
  id: number;
  isUnsolemnized: boolean;
  solemnizedDate?: string;
  solemnizedAge?: number;
  solemnizedByPastor?: string;
  marriagePhotos?: string[] | null;
  status?: string;
}

interface FamilyRelation {
  id: number;
  memberId: number;
  relatedMemberId: number | null;
  relatedNonMemberId: number | null;
  relationType: string;
  customRelationName?: string | null;
  fromDate?: string | null;
  toDate?: string | null;
  isActive: boolean;
  relatedMember?: RelatedMember | null;
  relatedNonMember?: RelatedNonMember | null;
  marriage?: Marriage;
}

interface FamilyTreeProps {
  familyData: {
    parents: FamilyRelation[];
    spouse: FamilyRelation | null;
    pastSpouses: FamilyRelation[];
    children: FamilyRelation[];
    siblings: FamilyRelation[];
  };
}

const FamilyTreeNode: React.FC<{ relation: FamilyRelation; relationType?: string }> = ({ relation, relationType }) => {
  const navigate = useNavigate();
  const relatedPerson = relation.relatedMember || relation.relatedNonMember;
  const name = relatedPerson?.name || "Unknown";
  const isNonMember = !!relation.relatedNonMember;
  const memberId = relatedPerson?.id;

  const handleClick = () => {
    // Only navigate if it's a member (not a non-member) and has an ID
    if (!isNonMember && memberId) {
      navigate(`/members/${memberId}`);
    }
  };

  return (
    <div className="flex flex-col items-center">
      <div 
        className={`p-3 rounded-lg border-2 min-w-[150px] transition-all ${
          isNonMember 
            ? "bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 cursor-default" 
            : "bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-700 cursor-pointer hover:bg-blue-100 dark:hover:bg-blue-900/30 hover:shadow-md"
        }`}
        onClick={handleClick}
        title={!isNonMember && memberId ? `View ${name}'s details` : undefined}
      >
        <div className="text-center">
          <div className="font-semibold text-gray-900 dark:text-white text-sm">{name}</div>
          {relatedPerson?.phone && (
            <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">{relatedPerson.phone}</div>
          )}
          {relationType && (
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {relation.customRelationName || relationType}
            </div>
          )}
          {isNonMember && (
            <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">(Non-Member)</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default function FamilyTree({ familyData }: FamilyTreeProps) {
  if (!familyData) return null;

  return (
    <div className="space-y-8">
      {/* Parents Section */}
      {familyData.parents.length > 0 && (
        <div className="space-y-4">
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
            Parents
          </h4>
          <div className="flex justify-center gap-6">
            {familyData.parents.map((parent) => (
              <FamilyTreeNode
                key={parent.id}
                relation={parent}
                relationType={parent.relationType}
              />
            ))}
          </div>
        </div>
      )}

      {/* Current Spouse Section */}
      {familyData.spouse && (
        <div className="space-y-4">
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
            Current Spouse
          </h4>
          <div className="flex justify-center gap-6">
            <div className="flex flex-col items-center">
              <FamilyTreeNode relation={familyData.spouse} relationType="Spouse" />
              {familyData.spouse.marriage && (
                <div className="mt-2 text-xs text-center text-gray-600 dark:text-gray-400 max-w-[150px]">
                  {familyData.spouse.marriage.isUnsolemnized ? (
                    <span className="text-orange-600 dark:text-orange-400">Un solemnized</span>
                  ) : (
                    <>
                      {familyData.spouse.marriage.solemnizedDate && (
                        <div>Date: {new Date(familyData.spouse.marriage.solemnizedDate).toLocaleDateString()}</div>
                      )}
                      {familyData.spouse.marriage.status && (
                        <div className={`mt-1 px-2 py-0.5 rounded text-xs inline-block ${
                          familyData.spouse.marriage.status === 'ACTIVE'
                            ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                            : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300'
                        }`}>
                          {familyData.spouse.marriage.status}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Past Spouses Section */}
      {familyData.pastSpouses.length > 0 && (
        <div className="space-y-4">
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
            Past Spouses
          </h4>
          <div className="flex justify-center gap-6">
            {familyData.pastSpouses.map((spouse) => (
              <div key={spouse.id} className="flex flex-col items-center">
                <FamilyTreeNode relation={spouse} relationType="Past Spouse" />
                {spouse.marriage && (
                  <div className="mt-2 text-xs text-center text-gray-600 dark:text-gray-400 max-w-[150px]">
                    {spouse.marriage.status && (
                      <div className={`mt-1 px-2 py-0.5 rounded text-xs inline-block ${
                        spouse.marriage.status === 'ACTIVE'
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                          : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300'
                      }`}>
                        {spouse.marriage.status}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Children Section */}
      {familyData.children.length > 0 && (
        <div className="space-y-4">
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
            Children
          </h4>
          <div className="flex flex-wrap justify-center gap-4">
            {familyData.children.map((child) => (
              <FamilyTreeNode
                key={child.id}
                relation={child}
                relationType={child.relationType}
              />
            ))}
          </div>
        </div>
      )}

      {/* Siblings Section */}
      {familyData.siblings.length > 0 && (
        <div className="space-y-4">
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
            Siblings
          </h4>
          <div className="flex flex-wrap justify-center gap-4">
            {familyData.siblings.map((sibling) => (
              <FamilyTreeNode
                key={sibling.id}
                relation={sibling}
                relationType={sibling.relationType}
              />
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {familyData.parents.length === 0 && 
       !familyData.spouse && 
       familyData.pastSpouses.length === 0 &&
       familyData.children.length === 0 && 
       familyData.siblings.length === 0 && (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          No family members added yet.
        </div>
      )}
    </div>
  );
}

